import clsx from "clsx";
import React, { FC } from "react";

import { CommonColors } from "../../../types/colors";
import { MaterialType } from "../../../types/materials";
import { BiesseLogo } from "../../biesse-logo";
import { ContactCard } from "../../cards/contact-card";
import { Container } from "../../container";
import { Pill } from "../../forms";
import { TagDimensions } from "../../forms/elements/tags/types";
import { HeroBannerProps } from "./hero-banner";
import { HeroBannerMedia } from "./hero-banner-media";
import { HeroBannerVariants, JustifyText } from "./types";

export interface HeroBannerMainContentProps extends Omit<HeroBannerProps, "strips"> {
  tagVariant?: HeroBannerVariants;
}

export const HeroBannerMainContent: FC<HeroBannerMainContentProps> = ({
  title,
  abstract,
  media,
  card,
  tagVariant = HeroBannerVariants.h2,
  justifyText = JustifyText.LEFT,
  color: _color,
  anchors,
  padded,
  showBiesseLogo = false,
  pillTags,
  customCard,
  mediaFit,
  backgroundImageUrl,
  isProduct,
}) => {
  const color = (_color as CommonColors) ?? CommonColors.Light;
  const titleClass = clsx(
    "text-light font-medium",
    tagVariant === HeroBannerVariants.h1 && "text-5xl lg:text-7xl",
    tagVariant === HeroBannerVariants.h2 && "text-4xl lg:text-6xl",
    justifyText === JustifyText.CENTER && "text-center"
  );
  const isMaterialVariant = _color && (Object.values(MaterialType) as string[]).includes(_color);
  return (
    <div
      className={clsx(
        padded && "rounded-4xl",
        !anchors && "lg:items-center",
        "w-full h-full relative flex flex-grow overflow-hidden [word-break:break-word] justify-center"
      )}
    >
      {/* Main content (title, description, optional material logo and card) */}
      <Container
        containerClassName={clsx(
          isMaterialVariant ? "pb-16 pt-12" : "py-16",
          !anchors ? "lg:items-center" : "lg:items-baseline",
          "flex flex-col lg:flex-row justify-between w-full z-20 gap-32"
        )}
        pagePaddingX
      >
        {(isMaterialVariant || title || abstract || showBiesseLogo || !!pillTags?.length) && (
          <div
            className={clsx(
              "flex flex-col gap-5 w-full",
              justifyText === JustifyText.CENTER && "items-center"
            )}
          >
            {!!pillTags?.length && (
              <div
                className={clsx(
                  "flex gap-2 w-full flex-wrap mb-8",
                  justifyText === JustifyText.CENTER && "justify-center"
                )}
              >
                {pillTags.map(({ color, children }, i) => {
                  return (
                    <Pill key={i} color={color} dimension={TagDimensions.Medium} checked readOnly>
                      {children}
                    </Pill>
                  );
                })}
              </div>
            )}
            {showBiesseLogo && (
              <BiesseLogo
                className="pb-2"
                material={(color as unknown as MaterialType) ?? undefined}
                color="white"
                monochrome
              />
            )}
            {title &&
              (tagVariant === HeroBannerVariants.h1 ? (
                <h1 className={titleClass}>{title}</h1>
              ) : (
                <h2 className={titleClass}>{title}</h2>
              ))}
            {abstract?.text && (
              <div
                className={clsx(
                  "text-light font-normal text-xl lg:max-w-3xl",
                  justifyText === JustifyText.CENTER && "text-center"
                )}
              >
                {abstract.text}
              </div>
            )}
          </div>
        )}
        {card && <ContactCard className="w-full md:w-auto lg:w-96 flex-none" {...card} />}
        {customCard}
      </Container>
      {(media || backgroundImageUrl) && (
        <>
          <HeroBannerMedia
            alt={title}
            media={media}
            mediaFit={mediaFit}
            backgroundImageUrl={backgroundImageUrl}
            tagVariant={tagVariant}
            isProduct={isProduct}
          />
          {(title || abstract) && <div className="absolute w-full h-full bg-dark opacity-40" />}
        </>
      )}
    </div>
  );
};
